// DataRateWithValidation.js - Wrapper for DataRate with rate card validation
import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { isFunction } from 'lodash-es'
import { Lock as LockIcon } from '@indeed/ifl-icons'

import DataRate from './DataRate'
import { currentIntl } from 'syft-acp-util/intl'

import './DataRateWithValidation.css'

const DataRateWithValidation = ({
  value,
  onChange,
  editable,
  showZero,
  disableOn,
  allData,
  disabled,
  suffix,
  allowEmpty,
  parameters = {},
}) => {
  const { amount = '', currency = currentIntl.currencyCode } = value || {}

  // Extract rate card data from parameters (comes from structure.js)
  // Parameters can be a function that needs to be called with allData
  const resolvedParameters = isFunction(parameters) ? parameters(allData) : parameters
  const {
    minRate,
    maxRate,
    rateCardId,
    isFixed,
    currency: rateCardCurrency,
    originalRate, // Original rate from pristine server data (for legacy data handling)
    validationError, // Optional custom error message
  } = resolvedParameters || {}

  const formatCurrency = rateAmount => {
    if (rateAmount == null) return 'N/A'
    const formattingCurrency = rateCardCurrency || currency
    return new Intl.NumberFormat(currentIntl.locale, {
      style: 'currency',
      currency: formattingCurrency,
    }).format(rateAmount)
  }

  // Check if rate has been modified from original (for legacy data handling)
  // If no originalRate exists, treat as modified (validate)
  // If originalRate exists and equals current, treat as unmodified (skip validation for legacy data)
  const isRateModified = originalRate == null || amount !== originalRate

  // Check if rate is within valid bounds - only validates if rate has been modified
  // This allows legacy data that's out of bounds to be saved unchanged
  const isOutOfBounds = () => {
    // If rate hasn't been modified, always valid (handles legacy data)
    if (!isRateModified) return false

    // If no rate card constraints, allow any value
    if (minRate == null || maxRate == null) return false

    // If no amount, it's invalid when rate card constraints exist
    if (amount === '' || amount == null) return true

    const numericAmount = parseFloat(amount)
    // Check for NaN (invalid number input)
    if (isNaN(numericAmount)) return true

    return numericAmount < minRate || numericAmount > maxRate
  }

  const isInvalid = isOutOfBounds()
  const isDisabled = disabled || isFixed

  return (
    <div className="data-rate-with-validation">
      <div className={classnames({ 'data-rate-with-validation--invalid': isInvalid })}>
        <DataRate
          value={value}
          onChange={onChange}
          editable={editable}
          showZero={showZero}
          disableOn={disableOn}
          allData={allData}
          disabled={isDisabled}
          suffix={suffix}
          allowEmpty={allowEmpty}
          parameters={parameters}
        />
      </div>

      {/* Rate card info */}
      {rateCardId && (
        <div className="data-rate-with-validation__info">
          {isFixed ? (
            <span className="data-rate-with-validation__fixed">
              <LockIcon size="sm" />
              <span>Fixed rate: {formatCurrency(minRate)}</span>
            </span>
          ) : (
            <span>
              Valid range: {formatCurrency(minRate)} - {formatCurrency(maxRate)}
            </span>
          )}
        </div>
      )}

      {/* Validation error */}
      {isInvalid && (
        <div className="data-rate-with-validation__error">
          {validationError ||
            `Rate must be between ${formatCurrency(minRate)} and ${formatCurrency(maxRate)}`}
        </div>
      )}
    </div>
  )
}

DataRateWithValidation.propTypes = {
  value: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  editable: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  showZero: PropTypes.bool,
  disableOn: PropTypes.array,
  allData: PropTypes.object,
  suffix: PropTypes.string,
  parameters: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      minRate: PropTypes.number,
      maxRate: PropTypes.number,
      rateCardId: PropTypes.number,
      isFixed: PropTypes.bool,
      currency: PropTypes.string,
      originalRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      validationError: PropTypes.string,
    }),
  ]),
  allowEmpty: PropTypes.bool,
}

DataRateWithValidation.defaultProps = {
  disabled: false,
  showZero: false,
  disableOn: null,
  allData: null,
  allowEmpty: false,
  suffix: '',
  parameters: {},
  value: null,
}

export default DataRateWithValidation
